import { recoverPending } from './peekSaved'
import type { Locale } from './constants'
import type { FortuneText } from './fortune'
import type { SaveResultPayload, StoredResult } from './resultPayload'
import { isNameParts, type NameParts } from './features/nameMaterial'
import type { TeaserKey } from './teaser'
import { tzHeader } from './userTz'
import { authHeadersOrEmpty } from '../auth/token'

/**
 * 서버 API 호출. 클라이언트는 Supabase 에 직접 붙지 않고 언제나 여기를 거친다.
 *
 * 키가 아직 안 꽂힌 로컬 환경에서는 Functions 가 503 not_configured 를 돌려준다.
 * 그때도 화면은 그대로 돌아가야 하므로, 저장은 "링크 없음" 으로 넘어가고
 * 맛보기는 일주별 기본 문장을 쓴다.
 */

export type SaveOutcome =
  /** `deleteKey` 는 **이때만** 온다. 기기 목록에 같이 담아 두어야 나중에 지울 수 있다. */
  | { ok: true; shortId: string; url: string; deleteKey?: string }
  | { ok: false; reason: 'not_configured' | 'failed' }

/**
 * 내 결과임을 서버에 알리는 헤더.
 * 이 기기가 만든 결과면 지우기 열쇠를, 로그인했으면 계정 토큰을 붙인다.
 * 열쇠를 주소(query)에 싣지 않는 이유: 주소는 기록에 남고 남에게 보내진다.
 */
export async function resultHeaders(resultId: string): Promise<Record<string, string>> {
  const headers: Record<string, string> = {}
  try {
    const { resultKeyOf } = await import('./deviceStore')
    const key = resultKeyOf(resultId)
    if (key) headers['x-result-key'] = key
  } catch {
    // 기기 저장소를 못 읽어도 계정 쪽으로 통과할 수 있다.
  }
  return { ...headers, ...(await authHeadersOrEmpty()) }
}

export async function saveResult(payload: SaveResultPayload): Promise<SaveOutcome> {
  try {
    /*
     * **로그인했으면 토큰을 붙인다**.
     *
     * 서버는 검사분인지 요청 표시와 **보낸 계정** 둘 다로 보는데(`isTestUseFor`),
     * 토큰이 없으면 계정을 알 길이 없어 검사 계정이 만든 결과가 실사용자로 저장됐다
     * (실측: `result_created` 5건이 통계에 올라갔다).
     */
    const res = await fetch('/api/result', {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...(await authHeadersOrEmpty()) },
      body: JSON.stringify(payload),
    })
    if (res.status === 503) return { ok: false, reason: 'not_configured' }
    if (!res.ok) return { ok: false, reason: 'failed' }
    const body = (await res.json()) as { shortId: string; url: string; deleteKey?: string }
    return { ok: true, shortId: body.shortId, url: body.url, deleteKey: body.deleteKey }
  } catch {
    return { ok: false, reason: 'failed' }
  }
}

export type LoadOutcome =
  | { ok: true; result: StoredResult }
  | { ok: false; reason: 'not_found' | 'not_configured' | 'failed' }

export async function loadResult(shortId: string): Promise<LoadOutcome> {
  try {
    // 주인만 받는다 — 이 기기의 지우기 열쇠나 계정 토큰을 붙인다
    const res = await fetch(`/api/result/${encodeURIComponent(shortId)}`, { headers: await resultHeaders(shortId) })
    if (res.status === 404) return { ok: false, reason: 'not_found' }
    if (res.status === 503) return { ok: false, reason: 'not_configured' }
    if (!res.ok) return { ok: false, reason: 'failed' }
    const body = (await res.json()) as { result: StoredResult }
    return { ok: true, result: body.result }
  } catch {
    return { ok: false, reason: 'failed' }
  }
}

/**
 * 결과 하나를 지운다. 풀이·타로·공유 카드도 함께 사라진다 (서버가 이어서 지운다).
 * 계정에 저장된 결과는 그 계정의 토큰이 있어야 지워진다.
 */
export async function deleteResult(shortId: string): Promise<boolean> {
  try {
    const res = await fetch(`/api/result/${encodeURIComponent(shortId)}/delete`, {
      method: 'POST',
      headers: await resultHeaders(shortId),
    })
    return res.ok
  } catch {
    return false
  }
}

/**
 * "오늘 왔다" 를 하루 한 번 남긴다 (DAU·MAU). 실패해도 화면은 그대로 간다.
 * 보내는 것은 기기 번호(또는 로그인 토큰)와 언어뿐이다.
 */
export async function recordVisit(locale: Locale, today: string): Promise<void> {
  try {
    const { visitorId, shouldCount, markCounted } = await import('./visitor')
    /*
     * **토큰을 먼저 본다**. 로그인했는지에 따라 "무엇으로 세는지" 가 갈리고,
     * 오늘 이미 그것으로 셌는지도 그 뒤에 판단해야 한다. 예전에는 날짜만 보고 넘겨서,
     * 로그인 전에 한 번 보낸 사람은 그날 로그인 방문이 남지 않았다.
     */
    const headers: Record<string, string> = { 'content-type': 'application/json' }
    try {
      const { accessToken } = await import('../auth/token')
      const token = await accessToken()
      if (token) headers.authorization = `Bearer ${token}`
    } catch {
      // 로그인 전이면 기기 번호로 센다
    }
    const as = headers.authorization ? 'account' : 'device'
    if (!shouldCount(today, as)) return
    const device = visitorId()
    if (!device && !headers.authorization) return
    /*
     * **서버가 무엇으로 셌는지 보고 적어 둔다**. 토큰을 보냈어도 만료 같은 이유로
     * 서버가 계정을 확인하지 못하면 기기로 세는데, 그때 "계정으로 셌다" 고 적어 두면
     * 그날은 로그인 방문이 영원히 남지 않는다. 서버 말을 따르면 다음 걸음에서 다시 센다.
     */
    const res = await fetch('/api/visit', { method: 'POST', headers, body: JSON.stringify({ device, locale }) })
    const said = res.ok ? ((await res.json().catch(() => null)) as { recorded?: boolean; signedIn?: boolean } | null) : null
    if (said?.recorded === false) return
    markCounted(today, said?.signedIn === true ? 'account' : 'device')
  } catch {
    // 방문 기록은 막을 일이 아니다
  }
}

export interface TeaserOutcome {
  /** null 이면 폴백이다. 고른 언어의 일주 기본 문장은 호출부가 채운다. */
  paragraphs: string[] | null
  source: 'ai' | 'cache' | 'fallback' | 'failed'
}

/**
 * 맛보기 문단.
 *
 * **못 받은 까닭을 가른다**.
 *   `failed`   요청 자체가 안 됐다 (끊김·5xx). 화면이 "다시 시도" 를 보여 준다.
 *   `fallback` 서버가 답은 했는데 글이 없다 (키 없음·AI 거절 등). 그때는 우리가 미리 쓴
 *              일주 문장을 조용히 보여 준다 — 다시 불러도 같은 답이라 시도할 이유가 없다.
 *
 * 예전에는 둘을 한 값으로 뭉쳐서, 끊겼을 때도 기본 문장이 조용히 나왔다.
 */
export async function fetchTeaser(key: TeaserKey): Promise<TeaserOutcome> {
  const failed: TeaserOutcome = { paragraphs: null, source: 'failed' }
  const fallback: TeaserOutcome = { paragraphs: null, source: 'fallback' }
  try {
    const res = await fetch('/api/teaser', {
      method: 'POST',
      // 토큰을 붙인다 — 로그인 없이도 되는 길목이지만, 붙여야 서버가
      // **검사 계정의 것**을 통계에서 뺄 수 있다 (실측: 맛보기 적중률이 검사로 늘어났다)
      headers: { 'content-type': 'application/json', ...(await authHeadersOrEmpty()) },
      body: JSON.stringify(key),
    })
    if (!res.ok) return failed
    const body = (await res.json()) as TeaserOutcome
    if (!Array.isArray(body.paragraphs) || body.paragraphs.length === 0) return fallback
    return body
  } catch {
    return failed
  }
}

export interface SpreadOutcome {
  /** 종합 해석. 실패하면 빈 문자열이고, 화면은 자리별 풀이만 보여 준다. */
  text: string
  source: 'ai' | 'none'
  reason?: string
  refunded?: number
  /** 켈틱 크로스는 **서버가** 포인트를 깎는다. 깎고 난 잔액을 함께 준다. */
  balance?: { paid: number; bonus: number }
}

/** 요청에 결과 번호가 있으면 그 결과의 열쇠까지, 없으면 계정 토큰만 */
async function spreadResultHeaders(request: unknown): Promise<Record<string, string>> {
  const id = (request as { resultId?: unknown } | null)?.resultId
  return typeof id === 'string' && id ? resultHeaders(id) : authHeadersOrEmpty()
}

/** 켈틱 크로스 종합 해석. 실패해도 오류를 보이지 않는다 — 자리별 풀이는 이미 화면에 있다. */
export async function readSpreadAi(request: unknown): Promise<SpreadOutcome> {
  // 응답 없이 끊겼으면(이유 없는 빈 응답) 같은 판을 확인만(?peek=1) 몇 번 더
  const first = await readSpreadAiOnce(request, false)
  // 서버가 '쓰는 중'(같은 판을 다른 요청이 쓰고 있다)이라 답해도 같은 길로 잠시 뒤 다시 읽는다
  return recoverPending(first, () => readSpreadAiOnce(request, true))
}

async function readSpreadAiOnce(request: unknown, peek: boolean): Promise<SpreadOutcome> {
  const empty: SpreadOutcome = { text: '', source: 'none' }
  try {
    const res = await fetch('/api/spread' + (peek ? '?peek=1' : ''), {
      method: 'POST',
      /* 포인트 잠금은 **서버**가 본다 (`requireUnlocked`/`spend`). 토큰을 안 보내면
         이미 산 사람도 `signin_required` 로 막혀 402 가 돌아온다 — 까지
         그래서 포인트만 깎이고 AI 는 한 번도 불리지 않았다. */
      // 결과 열쇠도 함께 — 값을 낸 사주를 서버가 계정에 담을 때 주인인지 본다
      headers: {
        'content-type': 'application/json',
        ...(await spreadResultHeaders(request)),
      },
      body: JSON.stringify(request),
    })
    /*
     * **402 의 몸통을 버리지 않는다**.
     *
     * 예전에는 `!res.ok` 면 `{text:'', source:'none'}` 만 돌려줬다. 그래서 값이
     * 모자라 막힌 것(402 `insufficient`)이 "글을 못 만들었다" 와 똑같이 보였고,
     * 화면은 "다시 시도" 를 내밀었다 — 몇 번을 눌러도 같은 402 라 영원히 안 됐다.
     * 이제 사유를 그대로 올려 보내 **충전 안내**로 바꿔 보여 준다.
     */
    const body = (await res.json().catch(() => null)) as SpreadOutcome | null
    if (!body || typeof body.text !== 'string') {
      return body?.reason ? { ...empty, reason: body.reason } : empty
    }
    return body
  } catch {
    // 응답을 못 받고 끊겼다
    return { ...empty, reason: 'dropped' }
  }
}

/**
 * 켈틱 크로스 한 판을 서버에 둔다 (결과 번호에 묶인다).
 * 실패해도 화면은 그대로다 — 브라우저 저장소가 남아 있다.
 */
export async function saveSpreadToServer(input: {
  resultId: string
  seed: string
  topic: string
  question: string
  summary: string
  locale: Locale
}): Promise<boolean> {
  try {
    const res = await fetch('/api/spreads', {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...(await resultHeaders(input.resultId)) },
      body: JSON.stringify(input),
    })
    if (!res.ok) return false
    const body = (await res.json()) as { saved?: boolean }
    return body?.saved === true
  } catch {
    return false
  }
}

/*
 * `pickLocale(viewer) => viewer` 를 여기서 없앴다.
 *
 * "보는 사람의 언어를 결과에 저장된 언어보다 우선한다" 는 옛 방침이었는데,
 * **아무 데서도 쓰이지 않았고** 지금 방침과 정반대다 — 결과의 풀이 언어는 만들 때
 * 정해지고 바뀌지 않는다. 그대로 두면 다음에 누가 집어 써서 이미 만든 풀이를
 * 다른 언어로 다시 부르게 된다 (AI 를 다시 부르는 = 돈이 나가는 길).
 * 화면 언어는 `locale`, 풀이 언어는 `readingLocale` 로 갈라 둔다 (App.tsx).
 */

export interface FortuneOutcome {
  /** null 이면 폴백. 호출부가 오행 관계별 기본 문장으로 채운다. */
  text: FortuneText | null
  source: 'ai' | 'cache' | 'fallback' | 'failed'
}

/** 오늘의 운세 문장. 보내는 값은 일주·오늘 일진·언어·사주로 정한 두 기운(와 횟수 제한용 결과 ID) 뿐이다. */
export async function fetchFortune(input: {
  dayPillar: string
  todayGanZhi: string
  locale: Locale
  /** 사주 기준 좋은 기운·멀리할 기운 (`sajuElements`) */
  lucky?: string
  /** null = 가장 많은 기운이 동점 — 보내지 않는다 */
  avoid?: string | null
  resultId?: string
}): Promise<FortuneOutcome> {
  try {
    const res = await fetch('/api/fortune', {
      method: 'POST',
      // 토큰을 붙인다 — 로그인 없이도 되는 길목이지만, 붙여야 서버가
      // **검사 계정의 것**을 통계에서 뺄 수 있다 (실측: 맛보기 적중률이 검사로 늘어났다)
      headers: { 'content-type': 'application/json', ...(await authHeadersOrEmpty()) },
      body: JSON.stringify(input),
    })
    if (!res.ok) return { text: null, source: 'fallback' }
    const body = (await res.json()) as FortuneOutcome
    return body.text ? body : { text: null, source: 'fallback' }
  } catch {
    return { text: null, source: 'fallback' }
  }
}

export interface CompatDetailOutcome {
  /** 구역 이름 → 본문. 연애는 여덟 자리, 그 밖은 다섯 자리. */
  parts: Record<string, string> | null
  source: 'ai' | 'cache' | 'fallback' | 'locked'
  /** 끝내 못 만들어 서버가 돌려준 값 */
  refunded?: number
  balance?: { paid: number; bonus: number }
}

/**
 * 궁합 상세 풀이. **두 사람의 사주 정보만** 보낸다.
 * 이름·별명·관계명은 이 함수의 인자에 아예 없다.
 */
export async function fetchCompatDetail(input: {
  a: { dayPillar: string; strongest: string; weakest: string; most?: string[]; least?: string[] }
  /** 상대 성별(사람 추가에서 고른 값) */
  b: { dayPillar: string; strongest: string; weakest: string; sex?: 'female' | 'male'; most?: string[]; least?: string[] }
  locale: Locale
  resultId: string
  /** 무슨 사이인지. 이 값으로 연애 궁합인지 일반 궁합인지 갈린다. */
  kind: string
  /** 상대 사람 번호 — 산 기록이 사람마다다 */
  personId: string
}): Promise<CompatDetailOutcome> {
  try {
    const res = await fetch('/api/compat', {
      method: 'POST',
      /* 포인트 잠금은 **서버**가 본다 (`requireUnlocked`/`spend`). 토큰을 안 보내면
         이미 산 사람도 `signin_required` 로 막혀 402 가 돌아온다 — 까지
         그래서 포인트만 깎이고 AI 는 한 번도 불리지 않았다. */
      headers: { 'content-type': 'application/json', ...(await authHeadersOrEmpty()), ...tzHeader() }, // 좋은/조심할 시기는 그 사람의 오늘 기준
      body: JSON.stringify(input),
    })
    if (res.status === 402) return { parts: {}, source: 'locked' }
    if (!res.ok) return { parts: null, source: 'fallback' }
    const body = (await res.json()) as CompatDetailOutcome
    // 서버가 끝내 못 만들어 돌려줬으면 그 값과 잔액을 함께 넘긴다
    return body.parts && Object.keys(body.parts).length > 0 ? body : { parts: null, source: 'fallback', ...(body.refunded ? { refunded: body.refunded } : {}), ...(body.balance ? { balance: body.balance } : {}) }
  } catch {
    return { parts: null, source: 'fallback' }
  }
}

/**
 * **셋이 보는 일 궁합** (같이 일할 사람 둘, 8P). 두 사람 궁합과 같은 모양에 `who` — 글의 {B}·{C} 가 누구인지(사람 번호).
 * 이름·생년월일은 보내지 않는다.
 */
export async function fetchCompatTeam(input: {
  a: { dayPillar: string; strongest: string; weakest: string; most?: string[]; least?: string[] }
  b: { dayPillar: string; strongest: string; weakest: string; sex?: 'female' | 'male'; most?: string[]; least?: string[] }
  c: { dayPillar: string; strongest: string; weakest: string; sex?: 'female' | 'male'; most?: string[]; least?: string[] }
  locale: Locale
  resultId: string
  personIds: [string, string]
}): Promise<CompatDetailOutcome & { who?: { B: string; C: string } }> {
  try {
    const res = await fetch('/api/compat/team', {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...(await authHeadersOrEmpty()), ...tzHeader() },
      body: JSON.stringify(input),
    })
    if (res.status === 402) return { parts: {}, source: 'locked' }
    if (!res.ok) return { parts: null, source: 'fallback' }
    const body = (await res.json()) as CompatDetailOutcome & { who?: { B: string; C: string } }
    return body.parts && Object.keys(body.parts).length > 0 ? body : { parts: null, source: 'fallback', ...(body.refunded ? { refunded: body.refunded } : {}), ...(body.balance ? { balance: body.balance } : {}) }
  } catch {
    return { parts: null, source: 'fallback' }
  }
}

export interface SectionOutcome {
  /** null 이면 폴백 (호출부가 규칙 기반 기본 문장으로 채운다) */
  parts: import('./report/spec').SectionParts | null
  source: 'ai' | 'cache' | 'fallback' | 'locked'
  /** 폴백일 때 서버가 준 까닭 (`ai_failed`·`locale_mismatch` …). 끊김·시간 초과면 없다 — 다시 쓸지 정하는 데 쓴다 */
  reason?: string
}

/** 리포트 한 장을 기다리는 최대 시간 — 넘으면 끊고 폴백(그 뒤 화면이 알아서 다시 쓴다). "작성 중" 이 끝없이 남지 않게 */
const SECTION_TIMEOUT_MS = 150_000

/**
 * 그 결과에 저장된 풀이 문장 전부. 결과를 다시 열 때 이것부터 받는다.
 * 저장돼 있으면 AI·캐시를 타지 않으므로 같은 결과는 늘 같은 문장이 나온다.
 */
/**
 * 결과마다 한 번만 묻는다. App 이 리포트의 기준 날짜를 정하려고 먼저 읽고, 곧이어 리포트 작업이
 * 같은 것을 읽는다 — 같은 때 겹친 요청과 30초 안의 다시 읽기는 같은 답을 쓴다.
 */
const savedSectionsCache = new Map<string, { at: number; ask: ReturnType<typeof loadSavedSections> }>()
/** 저장된 장 + 처음 저장된 시각 (App 이 리포트 기준 날짜를 정할 때) */
export function fetchSavedReport(resultId: string): ReturnType<typeof loadSavedSections> {
  const hit = savedSectionsCache.get(resultId)
  if (hit && Date.now() - hit.at < 30_000) return hit.ask
  const ask = loadSavedSections(resultId)
  savedSectionsCache.set(resultId, { at: Date.now(), ask })
  return ask
}
export async function fetchSavedSections(resultId: string): Promise<Awaited<ReturnType<typeof loadSavedSections>>['sections']> {
  return (await fetchSavedReport(resultId)).sections
}

/** 저장된 장과 처음 저장된 시각 (`madeAt` — 리포트 기준 날짜를 글을 쓴 때로 맞춘다) */
async function loadSavedSections(resultId: string): Promise<{ sections: Partial<Record<import('./report/spec').AiSection, import('./report/spec').SectionParts>>; madeAt: string | null }> {
  const { status, body } = await getJson<{ sections: Partial<Record<import('./report/spec').AiSection, import('./report/spec').SectionParts>>; madeAt?: string | null }>(
    `/api/result/${resultId}/sections`,
    await resultHeaders(resultId),
  )
  return status === 200 && body?.sections ? { sections: body.sections, madeAt: body.madeAt ?? null } : { sections: {}, madeAt: null }
}

/** 리포트 섹션 하나. 보내는 값은 네 기둥·대운 간지·기준 날짜·연애 상태 묶음·언어와 결과 id 뿐이다. */
export async function fetchSection(
  section: import('./report/spec').AiSection,
  input: import('./report/spec').ReportInput,
  resultId?: string,
  signal?: AbortSignal,
): Promise<SectionOutcome> {
  const timer = new AbortController()
  const stop = window.setTimeout(() => timer.abort(), SECTION_TIMEOUT_MS)
  signal?.addEventListener('abort', () => timer.abort())
  try {
    /* 포인트 잠금은 **서버**가 본다 (`requireUnlocked`/`spend`). 토큰을 안 보내면
       이미 산 사람도 `signin_required` 로 막혀 402 가 돌아온다 — 까지
       그래서 포인트만 깎이고 AI 는 한 번도 불리지 않았다. */
    // 결과 번호를 보내면 서버가 주인인지 본다 — 열쇠도 함께. 401 이면 토큰을 새로 받아 한 번 더
    const headers = { 'content-type': 'application/json', ...(resultId ? await resultHeaders(resultId) : await authHeadersOrEmpty()) }
    const res = await withFreshToken(headers, (h) => fetch('/api/section', {
      method: 'POST',
      headers: h,
      body: JSON.stringify({ section, input, ...(resultId ? { resultId } : {}) }),
      signal: timer.signal,
    }))
    if (res.status === 402) return { parts: null, source: 'locked' }
    if (!res.ok) return { parts: null, source: 'fallback' }
    const body = (await res.json()) as SectionOutcome
    return body.parts ? body : { parts: null, source: 'fallback', ...(typeof body.reason === 'string' ? { reason: body.reason } : {}) }
  } catch {
    return { parts: null, source: 'fallback' }
  } finally {
    window.clearTimeout(stop)
  }
}

// 공유 · 초대 · 결과 요약

/**
 * 토큰을 실어 보냈는데 401 이면(오래 켜 둔 탭에서 토큰이 끝났다) **한 번 새로 받아 그 요청을 다시** 보낸다.
 * 토큰 없이 보낸 요청은 그대로 둔다 (로그인 안 한 사람).
 */
async function withFreshToken(extra: Record<string, string> | undefined, send: (headers: Record<string, string>) => Promise<Response>): Promise<Response> {
  const res = await send(extra ?? {})
  if (res.status !== 401 || !extra?.authorization) return res
  const { accessToken } = await import('../auth/token')
  const token = await accessToken(true)
  return token ? send({ ...extra, authorization: `Bearer ${token}` }) : res
}

async function postJson<T>(path: string, body: unknown, extra?: Record<string, string>): Promise<{ status: number; body: T | null }> {
  try {
    const res = await withFreshToken(extra, (h) => fetch(path, { method: 'POST', headers: { 'content-type': 'application/json', ...h }, body: JSON.stringify(body) }))
    return { status: res.status, body: (await res.json().catch(() => null)) as T | null }
  } catch {
    return { status: 0, body: null }
  }
}

async function getJson<T>(path: string, extra?: Record<string, string>): Promise<{ status: number; body: T | null }> {
  try {
    const res = await withFreshToken(extra, (h) => fetch(path, Object.keys(h).length ? { headers: h } : undefined))
    return { status: res.status, body: (await res.json().catch(() => null)) as T | null }
  } catch {
    return { status: 0, body: null }
  }
}

/** 공유 페이지를 만들고 id 를 받는다. 실패하면 null. */
export async function createShare(share: import('./share').ShareRecord): Promise<string | null> {
  // 로그인 토큰도 함께 — 산 궁합의 공유는 서버가 산 사람인지 본다. 다른 공유에는 쓰지 않는다.
  const { status, body } = await postJson<{ id: string }>('/api/share', share, await authHeadersOrEmpty())
  return status === 201 && body?.id ? body.id : null
}

export type LoadedShare = import('./share').ShareRecord & {
  id: string
  /** 운세 공유일 때 그날 캐시된 한 줄 */
  line: string | null
}

/**
 * 공유받은 궁합의 자세한 글. 산 사람이 받은 글을 서버가 캐시에서 꺼내 준다 —
 * AI 를 부르지 않고 값도 받지 않는다. 없으면 null (화면은 점수와 무료 글만 보인다).
 */
export async function loadSharedCompat(id: string): Promise<Record<string, string> | null> {
  const { status, body } = await getJson<{ parts: Record<string, string> | null }>(`/api/share/${encodeURIComponent(id)}/compat`)
  return status === 200 && body?.parts ? body.parts : null
}

export async function loadShare(id: string): Promise<LoadedShare | 'not_found' | null> {
  const { status, body } = await getJson<LoadedShare>(`/api/share/${encodeURIComponent(id)}`)
  if (status === 404) return 'not_found'
  return status === 200 && body ? body : null
}

/** 질문 타로 한 판. 보내는 질문은 **정해진 번호**다 (자유 문장이 아니다). */
export interface AskInput {
  resultId: string
  questionId: string
  locale: Locale
  seed: string
  dayMaster?: string
  cards: { position: string; label?: string; name: string; direction: 'upright' | 'reversed'; summary: string; body: string }[]
  resume?: boolean
}

export type AskOutcome =
  | { ok: true; overall: string; lines: string[]; balance?: { paid: number; bonus: number } }
  | { ok: false; reason: string }

/**
 * 질문 타로 풀이. 로그인했으면 토큰을 함께 보낸다 — 서버가 그 계정으로 저장한다
 * (타로·초대에서 두 번 빠뜨렸던 자리다).
 */
export async function askReading(input: AskInput): Promise<AskOutcome> {
  // 응답 없이 끊겼으면 같은 판을 확인만(?peek=1) 몇 번 더
  const first = await askReadingOnce(input, false)
  // '쓰는 중' 이면 잠시 뒤 같은 판을 다시 읽어 같은 글을 보인다 (AI·값 없음)
  return recoverPending(first, () => askReadingOnce(input, true))
}

async function askReadingOnce(input: AskInput, peek: boolean): Promise<AskOutcome> {
  const { status, body } = await postJson<{
    ok?: boolean
    overall?: string
    lines?: string[]
    reason?: string
    balance?: { paid: number; bonus: number }
  }>('/api/ask' + (peek ? '?peek=1' : ''), input, await resultHeaders(input.resultId))
  // 응답을 못 받고 끊긴 것(status 0)은 서버의 실패와 가른다
  if (status === 0) return { ok: false, reason: 'dropped' }
  if (status === 200 && body?.ok && typeof body.overall === 'string' && Array.isArray(body.lines)) {
    // 서버가 깎은 뒤의 잔액 — 머리글 숫자를 바로 바꾼다
    return { ok: true, overall: body.overall, lines: body.lines, ...(body.balance ? { balance: body.balance } : {}) }
  }
  return { ok: false, reason: body?.reason ?? 'failed' }
}

/* ------------------------- 빠르게 물어보기 ------------------------- */

/**
 * YES or NO · 둘 중 뭐가 나을까.
 *
 * **질문은 사용자가 쓴 자유 문장이다** — 서버가 화면과 같은 함수(`checkQuestion`)로
 * 다시 보고, 걸리면 값을 깎기 전에 거절한다. 카드 번호를 함께 보내 서버가 씨앗과
 * 맞는지 확인한다 (J1 과 같은 규칙).
 */
export interface QuickInput {
  mode: 'yesno' | 'compare'
  resultId: string
  locale: Locale
  seed: string
  question: string
  options?: [string, string]
  dayMaster?: string
  cards: { id: number; name: string; direction: 'upright' | 'reversed'; summary: string; body: string; option?: string }[]
  /** 기록에 남은 판을 이어서 — 낸 판이 아니면 서버가 깎지 않고 locked */
  resume?: boolean
}

export type QuickOutcome =
  | { ok: true; answer?: 'yes' | 'no'; why?: string; a?: string; b?: string; pick?: string; balance?: { paid: number; bonus: number } }
  | { ok: false; reason: string; secondsLeft?: number }

export async function quickReading(input: QuickInput): Promise<QuickOutcome> {
  // 응답 없이 끊겼으면 같은 판을 확인만(?peek=1) 몇 번 더
  const first = await quickReadingOnce(input, false)
  // '쓰는 중' 이면 잠시 뒤 같은 판을 다시 읽어 같은 글을 보인다 (AI·값 없음)
  return recoverPending(first, () => quickReadingOnce(input, true))
}

async function quickReadingOnce(input: QuickInput, peek: boolean): Promise<QuickOutcome> {
  // 하루 무료는 이 사람의 시간대로 — 시간대 이름을 함께
  const { status, body } = await postJson<Record<string, unknown>>('/api/quick' + (peek ? '?peek=1' : ''), input, { ...(await resultHeaders(input.resultId)), ...tzHeader() })
  if (status === 0) return { ok: false, reason: 'dropped' }
  if (status === 200 && body?.ok === true) return { ok: true, ...(body as object) } as QuickOutcome
  // 하루 몫을 다 썼으면 다음 때까지 남은 초
  return { ok: false, reason: (body?.reason as string) ?? 'failed', ...(typeof body?.secondsLeft === 'number' ? { secondsLeft: body.secondsLeft } : {}) }
}

/**
 * 오늘 YES or NO 무료 몫을 썼는가 (서버가 판정한다 — 기기 시계를 믿지 않는다).
 * `admin` 이면 하루 한 번 제한이 없다.
 */
export async function quickFreeUsed(): Promise<{ signedIn: boolean; used: boolean; admin: boolean; secondsLeft?: number }> {
  const { status, body } = await getJson<{ signedIn?: boolean; used?: boolean; admin?: boolean; secondsLeft?: number }>('/api/quick?mode=yesno', { ...(await authHeadersOrEmpty()), ...tzHeader() })
  if (status !== 200 || !body) return { signedIn: false, used: false, admin: false }
  // 다 썼으면 다음 때까지 남은 초 (출석의 "다음 출석까지" 와 같은 셈)
  return { signedIn: body.signedIn === true, used: body.used === true, admin: body.admin === true, ...(typeof body.secondsLeft === 'number' ? { secondsLeft: body.secondsLeft } : {}) }
}

/**
 * **이름 풀이 본문 세 토막**.
 *
 * 계산은 화면이 끝내고 재료만 보낸다. 서버는 내용으로 키를 잡는 공유 캐시를 먼저 보므로
 * 같은 이름·같은 사주·같은 언어면 AI 를 부르지 않는다 (`source: 'cache'`).
 * 못 만들면 화면이 고정 문장으로 떨어진다 — 여기서 지어내지 않는다.
 */
export async function nameReadingText(
  material: unknown,
  /** 지난 기록을 여는 자리 — 캐시에 없으면 AI 를 부르지 않고 그대로 끝낸다 */
  cacheOnly = false,
  /** 어느 결과(사주)에서 묻는가 — 서버가 **그 사용자가 이미 푼 글**을 찾을 때 쓴다 */
  resultId?: string,
): Promise<
  | { ok: true; parts: NameParts; source: 'ai' | 'cache' | 'record'; aiKey?: string }
  | { ok: false; reason: string; refunded?: number; balance?: { paid: number; bonus: number } }
> {
  const payload = {
    ...(material as object),
    ...(cacheOnly ? { cacheOnly: true } : {}),
    ...(resultId ? { resultId } : {}),
  }
  const { status, body } = await postJson<Record<string, unknown>>('/api/name', payload, await authHeadersOrEmpty())
  if (status === 200 && body?.ok === true && isNameParts(body.parts)) {
    const source = body.source === 'cache' || body.source === 'record' ? body.source : 'ai'
    return { ok: true, parts: body.parts, source, ...(typeof body.aiKey === 'string' ? { aiKey: body.aiKey } : {}) }
  }
  return { ok: false, reason: (body?.reason as string) ?? 'failed', ...(typeof body?.refunded === 'number' ? { refunded: body.refunded } : {}),
    ...(body?.balance && typeof body.balance === 'object' ? { balance: body.balance as { paid: number; bonus: number } } : {}) }
}

/** 결과 id 들의 일주·저장 날짜 (내 결과 이어보기) */
export async function fetchResultSummaries(ids: string[]): Promise<{ id: string; dayPillar: string; savedOn: string; locale?: string; sig?: string }[]> {
  if (ids.length === 0) return []
  const { status, body } = await postJson<{ results: { id: string; dayPillar: string; savedOn: string; locale?: string; sig?: string }[] }>(
    '/api/results/summary',
    { ids: ids.slice(0, 50) },
  )
  return status === 200 && body ? body.results : []
}

/** 화면 조회 수 한 번 (이름·언어만). 실패해도 아무 일 없다. */
export function trackView(section: import('./admin').TrackedSection, locale: Locale): void {
  try {
    // 토큰을 붙인다 — 서버가 검사 계정의 조회를 빼려면 누구인지 알아야 한다.
    // 방문 기록(`/api/visit`)이 이미 같은 값을 붙이므로 새로 받는 것은 없다.
    void authHeadersOrEmpty().then((auth) =>
      fetch('/api/track', {
        method: 'POST',
        headers: { 'content-type': 'application/json', ...auth },
        body: JSON.stringify({ section, locale }),
        keepalive: true,
      }),
    ).catch(() => undefined)
  } catch {
    // 집계는 화면을 막지 않는다.
  }
}

/**
 * 이름에 든 한자를 어떻게 읽는지 서버에 묻는다 (H).
 *
 * **사전은 서버에만 있다** — 화면에 사전이 보이면 안 되고 한자를 읽는 용도로만
 * 쓴다는 조건이라, 번들에 넣지 않고 글자만 보내 음을 받아 온다. AI 는 부르지 않는다.
 * 못 받으면 빈 표를 돌려준다 (화면은 "읽지 못함" 으로 둔다).
 */
/**
 * 이름 풀이 대상 값을 만들 때 쓰는 읽기 — **못 받았으면 null** (빈 표와 구별한다).
 * 빈 표로 값을 만들면 서버가 사전으로 만든 값과 어긋나 "깎이고도 안 열린다". 그때는 사지 않게 한다.
 */
export async function fetchHanjaReadingsStrict(chars: string, locale: Locale): Promise<Record<string, string> | null> {
  if (chars === '') return {}
  try {
    const res = await fetch('/api/hanja', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ chars, locale }),
    })
    if (!res.ok) return null
    const body = (await res.json()) as { readings?: Record<string, string> }
    return body?.readings ?? null
  } catch {
    return null
  }
}

export async function fetchHanjaReadings(chars: string, locale: Locale): Promise<Record<string, string>> {
  if (chars === '') return {}
  try {
    const res = await fetch('/api/hanja', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ chars, locale }),
    })
    if (!res.ok) return {}
    const body = (await res.json()) as { readings?: Record<string, string> }
    return body?.readings ?? {}
  } catch {
    return {}
  }
}
